import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { appendAuditLogEntry } from '../../utils/auditLog';
import { resolveAdminRole } from '../../utils/adminRole';
import {
  DEFAULT_SITE_CONTENT,
  getSiteContent,
  resetSiteContent,
  saveSiteContent,
} from '../../utils/siteContent';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const cloneValue = (value) => JSON.parse(JSON.stringify(value));

const textareaStyle = {
  width: '100%',
  minHeight: '110px',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid #385f4f',
  background: '#f8fbf8',
  color: '#163127',
  fontSize: '14px',
  lineHeight: 1.5,
  resize: 'vertical',
  boxSizing: 'border-box',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid #385f4f',
  background: '#f8fbf8',
  color: '#163127',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'grid',
  gap: '8px',
  color: '#eaf5ef',
  fontSize: '13px',
  fontWeight: 700,
};

const sectionCardStyle = {
  background: '#1a2e25',
  border: '1px solid #2d4f3c',
  borderRadius: '18px',
  padding: '20px',
  display: 'grid',
  gap: '16px',
};

const fieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '14px',
};

const sectionTitleStyle = {
  margin: 0,
  color: '#f4fff7',
  fontSize: '18px',
};

const helperStyle = {
  margin: 0,
  color: '#b9d1c4',
  fontSize: '13px',
  lineHeight: 1.5,
};

const fieldSetStyle = {
  margin: 0,
  padding: 0,
  border: '0',
  display: 'grid',
  gap: '12px',
};

const SiteContentManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [content, setContent] = useState(() => cloneValue(getSiteContent()));
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState('');

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error: rpcError } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (rpcError || !isAdmin) {
          navigate('/account');
          return;
        }

        await resolveAdminRole(session.user.email);
        setAdminInfo({
          email: session.user.email,
          lastSignIn: session.user.last_sign_in_at,
        });
      } catch (err) {
        console.error('Site content admin access check error:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  const updateSectionField = (section, field, value) => {
    setContent((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
  };

  const updateNestedArrayItem = (section, field, index, nestedField, value) => {
    setContent((current) => {
      const nextSection = cloneValue(current[section]);
      nextSection[field][index] = {
        ...nextSection[field][index],
        [nestedField]: value,
      };
      return {
        ...current,
        [section]: nextSection,
      };
    });
  };

  const updateListFromText = (section, field, value) => {
    setContent((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value.split('\n').map((line) => line.trim()).filter(Boolean),
      },
    }));
  };

  const joinList = (items) => items.join('\n');

  const handleSaveRequest = (event) => {
    event.preventDefault();
    setShowConfirmSave(true);
  };

  const handleSave = () => {
    try {
      saveSiteContent(content);
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Updated',
        entityType: 'website content',
        entityName: 'Site content',
        details: 'Updated editable public website copy across the home, Trust, Social, form/enquiry, About, Terms, and Privacy pages.',
      });
      setLastSavedAt(new Date().toLocaleString('en-GB'));
      setMessage('Website content published successfully.');
      setError('');
      setShowConfirmSave(false);
    } catch (err) {
      console.error('Unable to save website content:', err);
      setError(err.message || 'Unable to save website content.');
      setShowConfirmSave(false);
    }
  };

  const handleReset = () => {
    resetSiteContent();
    setContent(cloneValue(DEFAULT_SITE_CONTENT));
    setMessage('Website content restored to defaults.');
    setError('');
    setLastSavedAt('');
  };

  const renderTextField = (section, field, label, textarea = false, rows = 3) => (
    <label style={labelStyle}>
      <span>{label}</span>
      {textarea ? (
        <textarea
          rows={rows}
          style={textareaStyle}
          value={content[section][field]}
          onChange={(event) => updateSectionField(section, field, event.target.value)}
        />
      ) : (
        <input
          style={inputStyle}
          value={content[section][field]}
          onChange={(event) => updateSectionField(section, field, event.target.value)}
        />
      )}
    </label>
  );

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={styles.mutedText}>Loading website content editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logo} />
        </div>

        <div style={styles.header}>
          <div style={styles.adminBadge}>Admin</div>
          <h2 style={styles.title}>Website Content Editor</h2>
          <p style={styles.subtitle}>Edit the public content boxes used across the site from one place.</p>
        </div>

        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Signed in as</span>
          <span style={styles.infoValue}>{adminInfo?.email}</span>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}
        {message && <div style={styles.successBanner}>{message}</div>}
        {lastSavedAt && <div style={styles.publishBanner}>Last published: {lastSavedAt}</div>}

        <form onSubmit={handleSaveRequest} style={styles.formLayout}>
          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Home page</h3>
            <p style={helperStyle}>Controls the launch page hero and the two main content cards.</p>
            <div style={fieldGridStyle}>
              {renderTextField('home', 'heroTitle', 'Hero title')}
              {renderTextField('home', 'heroDescription', 'Hero description', true, 4)}
              {renderTextField('home', 'heroStatNumber', 'Hero stat number')}
              {renderTextField('home', 'heroStatLabel', 'Hero stat label')}
              {renderTextField('home', 'trustCardTitle', 'Trust card title')}
              {renderTextField('home', 'trustCardDescription', 'Trust card description', true, 3)}
              {renderTextField('home', 'socialCardTitle', 'Social card title')}
              {renderTextField('home', 'socialCardDescription', 'Social card description', true, 3)}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>About page</h3>
            <p style={helperStyle}>All introductory and supporting text shown in the About page cards.</p>
            <div style={fieldSetStyle}>
              {renderTextField('about', 'introOne', 'Intro paragraph 1', true, 4)}
              {renderTextField('about', 'introTwo', 'Intro paragraph 2', true, 4)}
              {renderTextField('about', 'goals', 'Goals', true, 4)}
              {renderTextField('about', 'achievements', 'Achievements', true, 4)}
              {renderTextField('about', 'purpose', 'Purpose', true, 4)}
              {renderTextField('about', 'motif', 'Motif', true, 4)}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Community page</h3>
            <p style={helperStyle}>Edit the community heading, mission statement, contact label, and four pillars.</p>
            <div style={fieldSetStyle}>
              {renderTextField('community', 'title', 'Page title')}
              {renderTextField('community', 'mission', 'Mission', true, 4)}
              {renderTextField('community', 'contactLabel', 'Contact label')}
            </div>
            <div style={fieldGridStyle}>
              {content.community.pillars.map((pillar, index) => (
                <div key={pillar.title || index} style={{ display: 'grid', gap: '12px', padding: '16px', borderRadius: '14px', background: '#163127', border: '1px solid #2d4f3c' }}>
                  <label style={labelStyle}>
                    <span>Pillar {index + 1} title</span>
                    <input
                      style={inputStyle}
                      value={pillar.title}
                      onChange={(event) => updateNestedArrayItem('community', 'pillars', index, 'title', event.target.value)}
                    />
                  </label>
                  <label style={labelStyle}>
                    <span>Pillar {index + 1} description</span>
                    <textarea
                      rows="4"
                      style={textareaStyle}
                      value={pillar.description}
                      onChange={(event) => updateNestedArrayItem('community', 'pillars', index, 'description', event.target.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Social page</h3>
            <p style={helperStyle}>Update the social hero, slideshow cards, and the offering cards shown below it.</p>
            <div style={fieldSetStyle}>
              {renderTextField('social', 'heroTitle', 'Hero title')}
              {renderTextField('social', 'heroDescription', 'Hero description', true, 4)}
              {renderTextField('social', 'sectionLabel', 'Section label')}
              {renderTextField('social', 'sectionTitle', 'Section title')}
            </div>
            <div style={fieldGridStyle}>
              {content.social.cards.map((card, index) => (
                <div key={card.title || index} style={{ display: 'grid', gap: '12px', padding: '16px', borderRadius: '14px', background: '#163127', border: '1px solid #2d4f3c' }}>
                  <label style={labelStyle}>
                    <span>Card {index + 1} title</span>
                    <input
                      style={inputStyle}
                      value={card.title}
                      onChange={(event) => updateNestedArrayItem('social', 'cards', index, 'title', event.target.value)}
                    />
                  </label>
                  <label style={labelStyle}>
                    <span>Card {index + 1} text</span>
                    <textarea
                      rows="4"
                      style={textareaStyle}
                      value={card.text}
                      onChange={(event) => updateNestedArrayItem('social', 'cards', index, 'text', event.target.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div style={fieldGridStyle}>
              {content.social.slides.map((slide, index) => (
                <div key={slide.title || index} style={{ display: 'grid', gap: '12px', padding: '16px', borderRadius: '14px', background: '#163127', border: '1px solid #2d4f3c' }}>
                  <label style={labelStyle}>
                    <span>Slide {index + 1} title</span>
                    <input
                      style={inputStyle}
                      value={slide.title}
                      onChange={(event) => updateNestedArrayItem('social', 'slides', index, 'title', event.target.value)}
                    />
                  </label>
                  <label style={labelStyle}>
                    <span>Slide {index + 1} text</span>
                    <textarea
                      rows="4"
                      style={textareaStyle}
                      value={slide.text}
                      onChange={(event) => updateNestedArrayItem('social', 'slides', index, 'text', event.target.value)}
                    />
                  </label>
                  <label style={labelStyle}>
                    <span>Slide {index + 1} color</span>
                    <input
                      style={inputStyle}
                      value={slide.color}
                      onChange={(event) => updateNestedArrayItem('social', 'slides', index, 'color', event.target.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Forms & enquiry pages</h3>
            <p style={helperStyle}>Control booking and enquiry form titles/subheaders for social suite enquiries, quick enquiries, and trust event booking.</p>
            <div style={fieldGridStyle}>
              {renderTextField('forms', 'socialSuiteEnquiryHeroTitle', 'Social suite enquiry hero title')}
              {renderTextField('forms', 'socialSuiteEnquiryHeroSubtitle', 'Social suite enquiry hero subtitle', true, 3)}
              {renderTextField('forms', 'socialSuiteEnquirySectionLabel', 'Social suite enquiry form label')}
              {renderTextField('forms', 'socialSuiteEnquirySectionTitle', 'Social suite enquiry form title')}
              {renderTextField('forms', 'socialQuickEnquiryTitle', 'Quick enquiry modal title')}
              {renderTextField('forms', 'socialQuickEnquirySubtitle', 'Quick enquiry modal subtitle', true, 3)}
              {renderTextField('forms', 'trustBookingFormTitle', 'Trust booking form title')}
              {renderTextField('forms', 'trustBookingFormSubtitlePrefix', 'Trust booking form subtitle prefix')}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Trust page</h3>
            <p style={helperStyle}>These fields control the Trust homepage hero and the category intro boxes.</p>
            <div style={fieldGridStyle}>
              {renderTextField('trust', 'heroTitle', 'Hero title')}
              {renderTextField('trust', 'heroDescription', 'Hero description', true, 4)}
              {renderTextField('trust', 'heroStatNumber', 'Hero stat number')}
              {renderTextField('trust', 'heroStatLabel', 'Hero stat label')}
              {renderTextField('trust', 'communityIntro', 'Community intro', true, 4)}
              {renderTextField('trust', 'sportsIntro', 'Sports intro', true, 4)}
              {renderTextField('trust', 'activitiesIntro', 'Activities intro', true, 4)}
              {renderTextField('trust', 'communityRoleTitle', 'Community role title')}
              {renderTextField('trust', 'communityRoleText', 'Community role text', true, 4)}
              {renderTextField('trust', 'sportsRoleTitle', 'Sports role title')}
              {renderTextField('trust', 'sportsRoleText', 'Sports role text', true, 4)}
              {renderTextField('trust', 'activitiesRoleTitle', 'Activities role title')}
              {renderTextField('trust', 'activitiesRoleText', 'Activities role text', true, 4)}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Terms of Service</h3>
            <p style={helperStyle}>Each checklist is edited as one item per line.</p>
            <div style={fieldSetStyle}>
              {renderTextField('terms', 'intro', 'Intro', true, 4)}
              <label style={labelStyle}>
                <span>Allowed items</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.terms.allowed)} onChange={(event) => updateListFromText('terms', 'allowed', event.target.value)} />
              </label>
              <label style={labelStyle}>
                <span>Disallowed items</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.terms.disallowed)} onChange={(event) => updateListFromText('terms', 'disallowed', event.target.value)} />
              </label>
              <label style={labelStyle}>
                <span>Responsibilities</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.terms.responsibilities)} onChange={(event) => updateListFromText('terms', 'responsibilities', event.target.value)} />
              </label>
              <label style={labelStyle}>
                <span>Rules</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.terms.rules)} onChange={(event) => updateListFromText('terms', 'rules', event.target.value)} />
              </label>
              {renderTextField('terms', 'legal', 'Legal notice', true, 4)}
            </div>
          </section>

          <section style={sectionCardStyle}>
            <h3 style={sectionTitleStyle}>Privacy Policy</h3>
            <p style={helperStyle}>Like the terms page, the policy lists are edited one item per line.</p>
            <div style={fieldSetStyle}>
              {renderTextField('privacy', 'intro', 'Intro', true, 4)}
              <label style={labelStyle}>
                <span>Information we collect</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.privacy.collect)} onChange={(event) => updateListFromText('privacy', 'collect', event.target.value)} />
              </label>
              <label style={labelStyle}>
                <span>How we use it</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.privacy.use)} onChange={(event) => updateListFromText('privacy', 'use', event.target.value)} />
              </label>
              <label style={labelStyle}>
                <span>What we do not do</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.privacy.notDo)} onChange={(event) => updateListFromText('privacy', 'notDo', event.target.value)} />
              </label>
              {renderTextField('privacy', 'cookies', 'Cookies', true, 4)}
              <label style={labelStyle}>
                <span>Your rights</span>
                <textarea style={textareaStyle} rows="5" value={joinList(content.privacy.rights)} onChange={(event) => updateListFromText('privacy', 'rights', event.target.value)} />
              </label>
              {renderTextField('privacy', 'security', 'Security', true, 4)}
              {renderTextField('privacy', 'changes', 'Changes', true, 4)}
              {renderTextField('privacy', 'contact', 'Contact', true, 4)}
            </div>
          </section>

          <div style={styles.actions}>
            <button type="submit" style={styles.primaryBtn}>Review changes</button>
            <button type="button" style={styles.secondaryBtn} onClick={handleReset}>Reset to defaults</button>
            <button type="button" style={styles.ghostBtn} onClick={() => navigate('/admin-account')}>Back to dashboard</button>
          </div>
        </form>
      </div>

      {showConfirmSave && (
        <div style={styles.modalBackdrop} role="presentation" onClick={() => setShowConfirmSave(false)}>
          <div style={styles.modalCard} role="dialog" aria-modal="true" aria-labelledby="site-content-confirm-title" onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalKicker}>Confirm publish</div>
            <h3 id="site-content-confirm-title" style={styles.modalTitle}>Publish updated website content?</h3>
            <p style={styles.modalText}>
              This will update the public content boxes across the website immediately.
            </p>
            <div style={styles.modalSummary}>
              <div><strong>Home title:</strong> {content.home.heroTitle}</div>
              <div><strong>Trust title:</strong> {content.trust.heroTitle}</div>
              <div><strong>Social title:</strong> {content.social.heroTitle}</div>
              <div><strong>Suite enquiry title:</strong> {content.forms.socialSuiteEnquiryHeroTitle}</div>
              <div><strong>Terms intro:</strong> {content.terms.intro}</div>
            </div>
            <div style={styles.modalActions}>
              <button type="button" style={styles.primaryBtn} onClick={handleSave}>Yes, publish changes</button>
              <button type="button" style={styles.secondaryBtn} onClick={() => setShowConfirmSave(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    background: 'linear-gradient(135deg, #0f1f18 0%, #1e3f32 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#1a2e25',
    border: '1px solid #2d4f3c',
    borderRadius: '16px',
    padding: 'clamp(18px, 4vw, 40px)',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: 'clamp(60px, 12vw, 80px)',
    width: 'auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  adminBadge: {
    display: 'inline-block',
    background: '#4ade80',
    color: '#0f1f18',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '10px',
    textTransform: 'uppercase',
  },
  title: {
    margin: '0 0 8px',
    color: '#f7fff9',
    fontSize: 'clamp(24px, 4vw, 36px)',
  },
  subtitle: {
    margin: 0,
    color: '#b8d0c2',
  },
  infoRow: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '20px',
    padding: '14px 16px',
    borderRadius: '12px',
    background: '#163127',
    border: '1px solid #2d4f3c',
    color: '#eaf5ef',
  },
  infoLabel: {
    color: '#b8d0c2',
    fontWeight: 700,
  },
  infoValue: {
    wordBreak: 'break-word',
  },
  errorBanner: {
    marginBottom: '14px',
    padding: '12px 14px',
    borderRadius: '12px',
    background: '#fee2e2',
    color: '#7f1d1d',
    border: '1px solid #fca5a5',
  },
  successBanner: {
    marginBottom: '14px',
    padding: '12px 14px',
    borderRadius: '12px',
    background: '#dcfce7',
    color: '#14532d',
    border: '1px solid #86efac',
  },
  publishBanner: {
    marginBottom: '14px',
    padding: '12px 14px',
    borderRadius: '12px',
    background: '#e6f4ea',
    color: '#1f4d32',
    border: '1px solid #9bd3ae',
  },
  formLayout: {
    display: 'grid',
    gap: '18px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },
  primaryBtn: {
    border: 'none',
    borderRadius: '12px',
    padding: '12px 16px',
    background: '#4ade80',
    color: '#0f1f18',
    fontWeight: 800,
    cursor: 'pointer',
  },
  secondaryBtn: {
    border: '1px solid #5b8270',
    borderRadius: '12px',
    padding: '12px 16px',
    background: '#f8fbf8',
    color: '#163127',
    fontWeight: 700,
    cursor: 'pointer',
  },
  ghostBtn: {
    border: '1px solid #365d4c',
    borderRadius: '12px',
    padding: '12px 16px',
    background: 'transparent',
    color: '#f4fff7',
    fontWeight: 700,
    cursor: 'pointer',
  },
  mutedText: {
    color: '#b8d0c2',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(4, 11, 8, 0.72)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 50,
  },
  modalCard: {
    width: '100%',
    maxWidth: '640px',
    background: '#163127',
    border: '1px solid #2d4f3c',
    borderRadius: '18px',
    padding: '24px',
    color: '#f4fff7',
    boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
  },
  modalKicker: {
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    fontSize: '11px',
    color: '#98c9ae',
    marginBottom: '10px',
  },
  modalTitle: {
    margin: '0 0 10px',
    fontSize: '24px',
  },
  modalText: {
    margin: '0 0 18px',
    color: '#cfe3d8',
    lineHeight: 1.5,
  },
  modalSummary: {
    display: 'grid',
    gap: '10px',
    marginBottom: '18px',
    padding: '14px',
    borderRadius: '14px',
    background: '#0f1f18',
    border: '1px solid #2d4f3c',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
};

export default SiteContentManagement;
